import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { Mail } from "lucide-react";
import type { EmailBroadcastProgress } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function EmailBroadcastControl() {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState<EmailBroadcastProgress | null>(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stop = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; stop.current = true; };
  }, []);

  async function request(path = "", body?: object) {
    const token = await getToken();
    let response: Response;
    try {
      response = await fetch(`/api/admin/email-broadcast${path}`, {
        method: body === undefined ? "GET" : "POST",
        headers: { Authorization: `Bearer ${token}`, ...(body === undefined ? {} : { "Content-Type": "application/json" }) },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: AbortSignal.timeout(45_000),
      });
    } catch {
      throw new Error("連線中斷或回應逾時。進度會保留；請重新讀取後接續，切勿另行整批重寄。");
    }
    const text = await response.text();
    let data;
    try { data = JSON.parse(text); }
    catch { throw new Error("伺服器暫時未能回應。已記錄的進度不會清除；請重新讀取進度，勿另建一批重寄。"); }
    if (!response.ok) throw new Error(data.error ?? `讀取失敗（HTTP ${response.status}）`);
    return data.broadcast as EmailBroadcastProgress | null;
  }

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const saved = await request();
      if (mounted.current) setProgress(saved);
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : "無法讀取進度");
    } finally { if (mounted.current) setLoading(false); }
  }

  async function run(retryFailed = false) {
    stop.current = false;
    setRunning(true);
    setError(null);
    try {
      // Creation itself never sends mail. Repeated clicks return the same day.
      let saved = progress ?? await request("", {});
      if (!saved) throw new Error("未能建立發送批次");
      if (mounted.current) setProgress(saved);
      let retry = retryFailed;
      while (!stop.current && (saved.pending > 0 || retry)) {
        const next = await request("/batch", { day: saved.day, retryFailed: retry });
        retry = false;
        if (!next) throw new Error("找不到已儲存的發送批次");
        saved = next;
        if (mounted.current) setProgress(saved);
        if (saved.issues.some(issue => issue.status === "sending")) {
          throw new Error("部分電郵尚未確認是否獲接收，已暫停。稍後可繼續，系統會沿用相同防重寄識別碼。");
        }
        if (saved.pending > 0 && !stop.current) {
          const wait = saved.busy ? 3000 : 1100;
          await new Promise(resolve => setTimeout(resolve, wait));
        }
      }
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : "連線中斷；請重新讀取已儲存進度");
    } finally { if (mounted.current) setRunning(false); }
  }

  return <>
    <Button variant="outline" size="sm" className="gap-1.5 text-violet-600 border-violet-300 hover:bg-violet-50 hover:text-violet-700"
      onClick={() => { setOpen(true); if (!running) void refresh(); }}>
      <Mail className="h-4 w-4" />{running ? "查看寄信進度" : "發送電郵"}
    </Button>
    <Dialog open={open} onOpenChange={value => { setOpen(value); if (!value) stop.current = true; }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>每日電郵發送</DialogTitle>
          <DialogDescription>每批最多 10 封。請保持此頁開啟以繼續處理；關閉或離開後會在目前小批次完成後暫停，之後可接續。</DialogDescription>
        </DialogHeader>
        {loading && <p role="status">讀取已儲存進度…</p>}
        {error && <p role="alert" className="text-sm text-red-600 whitespace-pre-wrap">{error}</p>}
        {progress ? <div className="space-y-3 text-sm">
          <p>{progress.day} · {progress.tenderCount} 項招標 · {progress.total} 位收件人</p>
          <progress className="w-full" aria-label="電郵處理進度" max={progress.total || 1}
            value={progress.accepted + progress.failed + progress.unknown} />
          <p aria-live="polite">已提交 {progress.accepted} · 待處理 {progress.pending} · 失敗 {progress.failed} · 需核對 {progress.unknown}</p>
          <p className="text-muted-foreground">「已提交」代表寄信服務已接收，不等於已送達收件箱。同日重按不會重寄已提交的電郵。</p>
          {progress.busy && <p>目前有一批處理中，請稍候。若連線曾中斷，鎖定會在 90 秒內自動解除。</p>}
          {progress.pending === 0 && <p className="font-medium">{progress.failed || progress.unknown ? "本批處理完畢，請查看下列問題。" : "今日所有電郵已提交，無需再次發送。"}</p>}
          {progress.issues.length > 0 && <div className="border rounded p-3 max-h-52 overflow-y-auto">
            <h3 className="font-medium mb-2">未確認／失敗名單及投遞提示</h3>
            {progress.issues.map(issue => <div key={issue.email} className="mb-2 break-words">
              <p>{issue.email}</p><p className="text-muted-foreground">{issue.error ?? issue.status}</p>
            </div>)}
          </div>}
        </div> : !loading && <p className="text-sm">確認向所有已啟用電郵通知的會員發送今日招標？開始前會先核對今日已寄紀錄，避免重寄。</p>}
        <div className="flex flex-wrap gap-2">
          {!running && !loading && (!progress || progress.pending > 0) &&
            <Button onClick={() => void run()} disabled={!!error && !progress}>{progress ? "繼續未完成發送" : "確認開始發送"}</Button>}
          {running && <Button variant="outline" onClick={() => { stop.current = true; }}>本批完成後暫停</Button>}
          {!running && !loading && !!progress?.failed &&
            <Button variant="outline" onClick={() => void run(true)}>重試明確失敗項目</Button>}
          <Button variant="outline" onClick={() => void refresh()} disabled={running || loading}>重新讀取進度</Button>
        </div>
      </DialogContent>
    </Dialog>
  </>;
}
